/**
 * useBackgroundSync
 * 
 * Extracted background sync and bandwidth management logic used by AuthContext.
 * Keeps AuthContext slimmer while preserving its public API.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BandwidthManager, BandwidthConstrainedSync, type BandwidthSettings, type BandwidthUsage } from '@/lib/sync/bandwidth-sync-engine';
import { detectLocalChanges, markRemoteCheckPerformed } from '@/lib/utils/changeDetection';
import { createLogger } from '@/lib/utils/logger';

const logger = createLogger('useBackgroundSync');

export interface BackgroundSyncState {
  isSyncing: boolean;
  lastSyncTime: Date | null;
  syncError: string | null;
  bandwidthUsage: BandwidthUsage | null;
  bandwidthSettings: BandwidthSettings | null;
}

export interface BackgroundSyncAPI extends BackgroundSyncState {
  initializeSync: () => Promise<void>;
  stopBackgroundSync: () => void;
  updateBandwidthSettings: (settings: Partial<BandwidthSettings>) => void;
  triggerManualSync: () => Promise<void>;
  resetSyncState: () => void;
}

export function useBackgroundSync(isAuthenticated: boolean, organizationId?: string | null): BackgroundSyncAPI {
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState<Date | null>(null);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [bandwidthUsage, setBandwidthUsage] = useState<BandwidthUsage | null>(null);
  const [bandwidthSettings, setBandwidthSettings] = useState<BandwidthSettings | null>(null);

  const isSyncingRef = useRef(false);
  const syncInitializedRef = useRef(false);
  const syncIntervalIdRef = useRef<number | null>(null);

  useEffect(() => {
    isSyncingRef.current = isSyncing;
  }, [isSyncing]);

  const SYNCABLE_TABLES = useMemo(() => [
    'certificates',
    'personnel',
    'equipment',
    'workOrders',
    'documents',
    'forms',
    'incidents',
    'inspections',
    'drills',
    'riskAssessments',
  ], []);

  const syncJob = useCallback(async () => {
    const settings = BandwidthManager.getSettings();

    if (!settings.autoSync || !navigator.onLine) {
      return;
    }

    if (isSyncingRef.current) {
      logger.info('Sync already in progress, skipping...');
      return;
    }

    try {
      const changeDetection = await detectLocalChanges();
      if (!changeDetection.shouldSync) {
        logger.info('Skipping sync - no changes detected', {
          reason: changeDetection.reason,
          pendingCount: changeDetection.pendingCount,
        });
        return;
      }

      logger.info('Sync triggered by change detection', {
        reason: changeDetection.reason,
        pendingCount: changeDetection.pendingCount,
        affectedTables: changeDetection.affectedTables,
      });

      setIsSyncing(true);
      setSyncError(null);

      logger.info('Running auto-sync', { interval: `${settings.autoSyncInterval / 1000 / 60} min` });
      const result = await BandwidthConstrainedSync.fullSync(SYNCABLE_TABLES, {
        organizationId: organizationId || undefined,
      });
      markRemoteCheckPerformed();
      setLastSyncTime(new Date());
      setBandwidthUsage(BandwidthManager.getUsageToday());

      logger.info('Sync complete', {
        tables: result.tablesProcessed,
        records: result.recordsSynced,
        kilobytes: (result.totalBytesUsed / 1024).toFixed(2)
      });
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Sync failed';
      setSyncError(errorMsg);
      logger.error('Auto-sync error', error);
    } finally {
      setIsSyncing(false);
    }
  }, [SYNCABLE_TABLES, organizationId]);

  const startBackgroundSync = useCallback(() => {
    logger.info('Starting background sync scheduler...');

    // Run once immediately
    // Don't trigger if not authenticated to avoid unnecessary work
    if (isAuthenticated) {
      void syncJob();
    }

    // Schedule recurring syncs
    const settings = BandwidthManager.getSettings();
    const id = window.setInterval(syncJob, settings.autoSyncInterval);
    syncIntervalIdRef.current = id;
    logger.info('Sync scheduler started', { interval: `${settings.autoSyncInterval / 1000} sec` });
  }, [isAuthenticated, syncJob]);

  const stopBackgroundSync = useCallback(() => {
    if (syncIntervalIdRef.current !== null) {
      clearInterval(syncIntervalIdRef.current);
      syncIntervalIdRef.current = null;
      logger.info('Sync scheduler stopped');
    }
    syncInitializedRef.current = false;
  }, []);

  const initializeSync = useCallback(async () => {
    if (syncInitializedRef.current) {
      logger.info('Sync already initialized, skipping...');
      return;
    }

    syncInitializedRef.current = true;
    logger.info('Initializing sync system...');

    try {
      await BandwidthManager.initialize({
        dailyLimitMB: 25,
        enableAtSeaMode: false,
        syncPriority: 'balanced',
        autoSync: true,
        autoSyncInterval: 300000, // 5 min
      });

      setBandwidthSettings(BandwidthManager.getSettings());
      setBandwidthUsage(BandwidthManager.getUsageToday());
      logger.info('Sync system initialized');
      startBackgroundSync();
    } catch (error) {
      logger.error('Failed to initialize sync:', error);
      setSyncError(error instanceof Error ? error.message : 'Failed to initialize sync');
      syncInitializedRef.current = false;
    }
  }, [startBackgroundSync]);

  const triggerManualSync = useCallback(async () => {
    if (!isAuthenticated) {
      logger.warn('Cannot sync: no user logged in');
      return;
    }
    if (isSyncing) {
      logger.warn('Sync already in progress');
      return;
    }
    try {
      setIsSyncing(true);
      setSyncError(null);
      logger.info('Starting manual sync...');
      const result = await BandwidthConstrainedSync.fullSync(SYNCABLE_TABLES, {
        organizationId: organizationId || undefined,
      });
      markRemoteCheckPerformed();
      setLastSyncTime(new Date());
      setBandwidthUsage(BandwidthManager.getUsageToday());
      logger.info('Manual sync complete', {
        tables: result.tablesProcessed,
        records: result.recordsSynced,
        kilobytes: (result.totalBytesUsed / 1024).toFixed(2)
      });
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Manual sync failed';
      setSyncError(errorMsg);
      logger.error('Manual sync error', error);
    } finally {
      setIsSyncing(false);
    }
  }, [SYNCABLE_TABLES, isAuthenticated, isSyncing, organizationId]);

  const updateBandwidthSettings = useCallback((settings: Partial<BandwidthSettings>) => {
    BandwidthManager.updateSettings(settings);
    setBandwidthSettings(BandwidthManager.getSettings());
    logger.info('Bandwidth settings updated', settings);
  }, []);

  const resetSyncState = useCallback(() => {
    setIsSyncing(false);
    setSyncError(null);
    setLastSyncTime(null);
    setBandwidthUsage(null);
    setBandwidthSettings(null);
  }, []);

  // Network listeners for auto behavior
  useEffect(() => {
    const handleOnline = () => {
      logger.info('Connection restored - triggering sync...');
      void triggerManualSync();
    };
    const handleOffline = () => {
      logger.info('Connection lost - using local cache');
    };
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [triggerManualSync]);

  return {
    isSyncing,
    lastSyncTime,
    syncError,
    bandwidthUsage,
    bandwidthSettings,
    initializeSync,
    stopBackgroundSync,
    updateBandwidthSettings,
    triggerManualSync,
    resetSyncState,
  };
}
